import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SubsDetail } from "../subs/subs.entity";
import * as subsApi from "../subs/subs.api";
import { uuid } from "@/interfaces";
import type { Service } from "@shyne/shared/api";
import type { CustomerContact, DocumentRecord, Subcontract } from "@shyne/shared/api";

interface PrimaryContact {
  name: string;
  email: string;
  phone?: string;
  designation?: string;
}

const primaryContactOf = (contact: CustomerContact | null): PrimaryContact | null =>
  contact === null
    ? null
    : { name: contact.name ?? "", email: contact.email ?? "", phone: contact.phone ?? undefined, designation: contact.designation ?? undefined };

const sameServices = (subcontract: Subcontract, selected: string[]) =>
  JSON.stringify(subcontract.services.map((s) => s.id).sort()) === JSON.stringify([...selected].sort());

const showSubcontract = (state: SubsDetailState, subcontract: Subcontract) => {
  state.subsDetail = subcontract;
  state.selectedServices = subcontract.services.map((s) => s.id);
  state.hasChanges = false;
  state.primaryContact = primaryContactOf(subcontract.contact);
};

interface SubsDetailState {
  subsDetail: SubsDetail | null;
  loading: boolean;
  updating: boolean;
  availableServices: Service[];
  servicesLoading: boolean;
  selectedServices: uuid[];
  hasChanges: boolean;
  primaryContact: PrimaryContact | null;
  contactModalOpen: boolean;
  contactSaving: boolean;
  documents: DocumentRecord[];
  documentsLoading: boolean;
  documentsTotal: number;
  documentsPage: number;
  documentsPageSize: number;
  uploadModalOpen: boolean;
  uploadingDocument: boolean;
  deletingDocumentId: uuid | null;
  updatingDocumentId: uuid | null;
}

const initialState: SubsDetailState = {
  subsDetail: null,
  loading: false,
  updating: false,
  availableServices: [],
  servicesLoading: false,
  selectedServices: [],
  hasChanges: false,
  primaryContact: null,
  contactModalOpen: false,
  contactSaving: false,
  documents: [],
  documentsLoading: false,
  documentsTotal: 0,
  documentsPage: 1,
  documentsPageSize: 10,
  uploadModalOpen: false,
  uploadingDocument: false,
  deletingDocumentId: null,
  updatingDocumentId: null,
};

export const fetchSubsDetail = createAsyncThunk("subsDetail/fetchSubsDetail", async (subsId: uuid) => {
  return subsApi.getSubsDetail(subsId);
});

export const updateSubsServices = createAsyncThunk(
  "subsDetail/updateSubsServices",
  async ({ subsId, services }: { subsId: uuid; services: uuid[] }) => subsApi.updateSubsServices(subsId, services)
);

export const fetchAvailableServices = createAsyncThunk("subsDetail/fetchAvailableServices", async () => {
  return subsApi.getAvailableServices();
});

export const updatePrimaryContact = createAsyncThunk(
  "subsDetail/updatePrimaryContact",
  async ({ subsId, contact }: { subsId: uuid; contact: PrimaryContact }) => subsApi.updatePrimaryContact(subsId, contact)
);

export const fetchSubsDocuments = createAsyncThunk(
  "subsDetail/fetchSubsDocuments",
  async ({ subsId, page, pageSize }: { subsId: uuid; page: number; pageSize: number }) => {
    return subsApi.listSubsDocuments(subsId, page, pageSize);
  }
);

export const uploadSubsDocument = createAsyncThunk(
  "subsDetail/uploadSubsDocument",
  async ({ subsId, file, expiresAt }: { subsId: uuid; file: File; expiresAt?: string | Date }) => subsApi.uploadSubsDocument(subsId, file, expiresAt)
);

export const deleteSubsDocument = createAsyncThunk("subsDetail/deleteSubsDocument", async (docId: uuid) => {
  await subsApi.deleteSubsDocument(docId);
  return docId;
});

export const updateSubsDocumentExpiresAt = createAsyncThunk(
  "subsDetail/updateSubsDocumentExpiresAt",
  async ({ docId, expiresAt }: { docId: uuid; expiresAt: string | Date | null }) => {
    return subsApi.updateSubsDocumentExpiresAt(docId, expiresAt);
  }
);

const updateDocumentInList = (documents: DocumentRecord[], updatedDocument: DocumentRecord) =>
  documents.map((document) => (document.id === updatedDocument.id ? updatedDocument : document));

const subsDetailSlice = createSlice({
  name: "subsDetail",
  initialState,
  reducers: {
    toggleService: (state, action) => {
      const serviceId = action.payload;
      if (state.selectedServices.includes(serviceId)) {
        state.selectedServices = state.selectedServices.filter((id) => id !== serviceId);
      } else {
        state.selectedServices.push(serviceId);
      }

      if (state.subsDetail) {
        state.hasChanges = !sameServices(state.subsDetail, state.selectedServices);
      }
    },
    setSelectedServices: (state, action) => {
      state.selectedServices = action.payload;

      if (state.subsDetail) {
        state.hasChanges = !sameServices(state.subsDetail, action.payload);
      }
    },
    setContactModalOpen: (state, action) => {
      state.contactModalOpen = action.payload;
    },
    setPrimaryContact: (state, action) => {
      state.primaryContact = action.payload;
    },
    setUploadDocumentModalOpen: (state, action) => {
      state.uploadModalOpen = action.payload;
    },
    setDocumentsPage: (state, action) => {
      state.documentsPage = action.payload;
    },
    setDocumentsPageSize: (state, action) => {
      state.documentsPageSize = action.payload;
      state.documentsPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubsDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubsDetail.fulfilled, (state, action) => {
        state.loading = false;
        showSubcontract(state, action.payload);
      })
      .addCase(fetchSubsDetail.rejected, (state) => {
        state.loading = false;
        state.subsDetail = null;
      })
      .addCase(updateSubsServices.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateSubsServices.fulfilled, (state, action) => {
        state.updating = false;
        showSubcontract(state, action.payload);
      })
      .addCase(updateSubsServices.rejected, (state) => {
        state.updating = false;
      })
      .addCase(fetchAvailableServices.pending, (state) => {
        state.servicesLoading = true;
      })
      .addCase(fetchAvailableServices.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.availableServices = action.payload.data || [];
      })
      .addCase(fetchAvailableServices.rejected, (state) => {
        state.servicesLoading = false;
      })
      .addCase(updatePrimaryContact.pending, (state) => {
        state.contactSaving = true;
      })
      .addCase(updatePrimaryContact.fulfilled, (state, action) => {
        state.contactSaving = false;
        state.contactModalOpen = false;
        state.subsDetail = action.payload;
        state.primaryContact = primaryContactOf(action.payload.contact);
      })
      .addCase(updatePrimaryContact.rejected, (state) => {
        state.contactSaving = false;
      })
      .addCase(fetchSubsDocuments.pending, (state) => {
        state.documentsLoading = true;
      })
      .addCase(fetchSubsDocuments.fulfilled, (state, action) => {
        state.documentsLoading = false;
        state.documents = action.payload.data;
        state.documentsTotal = action.payload.count;
      })
      .addCase(fetchSubsDocuments.rejected, (state) => {
        state.documentsLoading = false;
      })
      .addCase(uploadSubsDocument.pending, (state) => {
        state.uploadingDocument = true;
      })
      .addCase(uploadSubsDocument.fulfilled, (state, action) => {
        state.uploadingDocument = false;
        state.uploadModalOpen = false;
        state.documentsPage = 1;
        state.documentsTotal += 1;
        state.documents.unshift(action.payload);
      })
      .addCase(uploadSubsDocument.rejected, (state) => {
        state.uploadingDocument = false;
      })
      .addCase(deleteSubsDocument.pending, (state, action) => {
        state.deletingDocumentId = action.meta.arg;
      })
      .addCase(deleteSubsDocument.fulfilled, (state, action) => {
        state.deletingDocumentId = null;
        state.documents = state.documents.filter((doc) => doc.id !== action.payload);
        state.documentsTotal = Math.max(0, state.documentsTotal - 1);
        if (state.documents.length === 0 && state.documentsPage > 1) {
          state.documentsPage -= 1;
        }
      })
      .addCase(deleteSubsDocument.rejected, (state) => {
        state.deletingDocumentId = null;
      })
      .addCase(updateSubsDocumentExpiresAt.pending, (state, action) => {
        state.updatingDocumentId = action.meta.arg.docId;
      })
      .addCase(updateSubsDocumentExpiresAt.fulfilled, (state, action) => {
        state.updatingDocumentId = null;
        state.documents = updateDocumentInList(state.documents, action.payload);
      })
      .addCase(updateSubsDocumentExpiresAt.rejected, (state) => {
        state.updatingDocumentId = null;
      });
  },
});

export const {
  toggleService,
  setSelectedServices,
  setContactModalOpen,
  setPrimaryContact,
  setUploadDocumentModalOpen,
  setDocumentsPage,
  setDocumentsPageSize,
} = subsDetailSlice.actions;

export default subsDetailSlice.reducer;
